/**
 * AcquisitionSync · MGM v1
 *
 * Sits inside AuthProvider. When a user is signed in and this browser holds an
 * attribution queued at signup for that same e-mail, sends it (src/lib/acquisition.ts).
 * This is the recovery path: the call made during signup may have been lost (Stripe
 * redirect, network), and the next signed-in page load, including the return from
 * Stripe, delivers it. The server answers "already_recorded" to a repeat.
 */
import { useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { flushAttribution, readPending } from '../lib/acquisition'

const RETRY_MS = [5_000, 30_000, 120_000]

export function AcquisitionSync() {
  const { user } = useAuth()
  const userId = user?.id ?? null
  const email = user?.email ?? null

  useEffect(() => {
    if (!userId || !email || !readPending()) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const attempt = async (n: number) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (cancelled) return
      const result = await flushAttribution({ email, accessToken: session?.access_token })
      if (result === 'failed' && !cancelled && n < RETRY_MS.length) timer = setTimeout(() => { void attempt(n + 1) }, RETRY_MS[n])
    }
    void attempt(0)
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [userId, email])

  return null
}
